import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { exclude } from '../../utils/array';

type MapSelection =
  | { kind: 'none' }
  | { kind: 'area'; areaKeyCodes: string[]; customAreaKeys: string[] }
  | { kind: 'link'; wayIds: string[] };

type UseMapSelection = {
  selectionKind: MapSelection['kind'];
  selectedAreaKeyCodes: string[];
  selectedCustomAreaKeys: string[];
  selectedWayIds: string[];
  countOfSelected: number;
  selectArea: (keyCode: string) => void;
  toggleAreaSelection: (keyCode: string) => void;
  selectCustomArea: (key: string) => void;
  toggleCustomAreaSelection: (key: string) => void;
  selectLink: (wayId: string) => void;
  toggleLinkSelection: (wayId: string) => void;
  clearSelection: () => void;
};

// 空配列を都度生成すると参照が変わり再レンダリングが起こるため固定する
const EMPTY_ARRAY = [] as const satisfies string[];

const mapSelectionAtom = atom<MapSelection>({ kind: 'none' });

const useMapSelection = (): UseMapSelection => {
  const [selection, setSelection] = useAtom(mapSelectionAtom);

  // エリアを選択するとリンク選択は破棄される（排他）
  const selectArea = useCallback(
    (keyCode: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'area') {
          return { kind: 'area', areaKeyCodes: [keyCode], customAreaKeys: [] };
        }
        if (current.areaKeyCodes.includes(keyCode)) {
          return current;
        }
        return { ...current, areaKeyCodes: [...current.areaKeyCodes, keyCode] };
      });
    },
    [setSelection]
  );

  const toggleAreaSelection = useCallback(
    (keyCode: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'area') {
          return { kind: 'area', areaKeyCodes: [keyCode], customAreaKeys: [] };
        }
        if (current.areaKeyCodes.includes(keyCode)) {
          if (
            current.areaKeyCodes.length === 1 &&
            current.customAreaKeys.length === 0
          ) {
            return { kind: 'none' };
          }
          return {
            ...current,
            areaKeyCodes: exclude(current.areaKeyCodes, keyCode),
          };
        }
        return { ...current, areaKeyCodes: [...current.areaKeyCodes, keyCode] };
      });
    },
    [setSelection]
  );

  const selectCustomArea = useCallback(
    (key: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'area') {
          return { kind: 'area', areaKeyCodes: [], customAreaKeys: [key] };
        }
        if (current.customAreaKeys.includes(key)) {
          return current;
        }
        return { ...current, customAreaKeys: [...current.customAreaKeys, key] };
      });
    },
    [setSelection]
  );

  const toggleCustomAreaSelection = useCallback(
    (key: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'area') {
          return { kind: 'area', areaKeyCodes: [], customAreaKeys: [key] };
        }
        if (current.customAreaKeys.includes(key)) {
          if (
            current.customAreaKeys.length === 1 &&
            current.areaKeyCodes.length === 0
          ) {
            return { kind: 'none' };
          }
          return {
            ...current,
            customAreaKeys: exclude(current.customAreaKeys, key),
          };
        }
        return { ...current, customAreaKeys: [...current.customAreaKeys, key] };
      });
    },
    [setSelection]
  );

  const selectLink = useCallback(
    (wayId: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'link') {
          return { kind: 'link', wayIds: [wayId] };
        }
        if (current.wayIds.includes(wayId)) {
          return current;
        }
        return { kind: 'link', wayIds: [...current.wayIds, wayId] };
      });
    },
    [setSelection]
  );

  const toggleLinkSelection = useCallback(
    (wayId: string): void => {
      setSelection((current: MapSelection): MapSelection => {
        if (current.kind !== 'link') {
          return { kind: 'link', wayIds: [wayId] };
        }
        if (current.wayIds.includes(wayId)) {
          if (current.wayIds.length === 1) {
            return { kind: 'none' };
          }
          return { kind: 'link', wayIds: exclude(current.wayIds, wayId) };
        }
        return { kind: 'link', wayIds: [...current.wayIds, wayId] };
      });
    },
    [setSelection]
  );

  const clearSelection = useCallback((): void => {
    setSelection({ kind: 'none' });
  }, [setSelection]);

  return {
    selectionKind: selection.kind,
    selectedAreaKeyCodes:
      selection.kind === 'area' ? selection.areaKeyCodes : EMPTY_ARRAY,
    selectedCustomAreaKeys:
      selection.kind === 'area' ? selection.customAreaKeys : EMPTY_ARRAY,
    selectedWayIds: selection.kind === 'link' ? selection.wayIds : EMPTY_ARRAY,
    countOfSelected:
      selection.kind === 'area'
        ? selection.areaKeyCodes.length + selection.customAreaKeys.length
        : selection.kind === 'link'
          ? selection.wayIds.length
          : 0,
    selectArea,
    toggleAreaSelection,
    selectCustomArea,
    toggleCustomAreaSelection,
    selectLink,
    toggleLinkSelection,
    clearSelection,
  };
};

export { useMapSelection };
